// src/components/ui/DiscountPercentagePopup.jsx
import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { X, Percent } from 'lucide-react';
import styles from './DiscountPercentagePopup.module.css';

const PRESETS = [3, 5, 10, 20, 25, 30, 50];
const POPUP_WIDTH = 228; // matches min-width in CSS

/**
 * Discount-percentage popup rendered into document.body via a React portal,
 * positioned with `position: fixed` relative to the trigger button element.
 * This escapes any ancestor overflow clipping.
 *
 * Props:
 *   anchorEl  {HTMLElement} – the % trigger button DOM node (for positioning)
 *   unitPrice {number}      – current line unit price
 *   quantity  {number}      – current line quantity
 *   onApply   {function}    – called with computed discount amount (number, 2 d.p.)
 *   onClose   {function}    – called when popup should close
 */
const DiscountPercentagePopup = ({ anchorEl, unitPrice, quantity, onApply, onClose }) => {
  const [percentage, setPercentage] = useState('');
  const inputRef = useRef(null);

  const subtotal = (parseFloat(unitPrice) || 0) * (parseInt(quantity) || 1);
  const pct = parseFloat(percentage);
  const discountAmount =
    !isNaN(pct) && pct > 0 && pct <= 100
      ? parseFloat((subtotal * pct / 100).toFixed(2))
      : null;

  // Compute fixed coords from the anchor button element.
  // Runs synchronously during render so there's no first-frame flicker.
  const style = useMemo(() => {
    if (!anchorEl) return { position: 'fixed', top: 0, left: 0, zIndex: 1400 };

    const rect = anchorEl.getBoundingClientRect();
    const viewportH = window.innerHeight;
    const viewportW = window.innerWidth;

    // Horizontal: align left edge of popup with the button, clamped to viewport
    let left = rect.left;
    if (left + POPUP_WIDTH > viewportW - 8) {
      left = Math.max(8, viewportW - POPUP_WIDTH - 8);
    }

    // Vertical: open below the button; if too close to the bottom, open above instead
    const spaceBelow = viewportH - rect.bottom;
    if (spaceBelow < 260) {
      // not enough room below — position above
      return { position: 'fixed', bottom: viewportH - rect.top + 4, left, zIndex: 1400 };
    }
    return { position: 'fixed', top: rect.bottom + 4, left, zIndex: 1400 };
  }, [anchorEl]);

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, []);

  const handleApply = () => {
    if (discountAmount !== null) {
      onApply(discountAmount);
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); handleApply(); }
    if (e.key === 'Escape') onClose();
  };

  return ReactDOM.createPortal(
    <>
      {/* Full-screen transparent backdrop — catches clicks outside the popup */}
      <div className={styles.backdrop} onClick={onClose} />

      <div className={styles.popup} style={style} role="dialog" aria-label="Discount percentage">
        <div className={styles.header}>
          <span className={styles.title}>
            <Percent size={12} />
            Discount %
          </span>
          <button type="button" className={styles.closeBtn} onClick={onClose} title="Close">
            <X size={13} />
          </button>
        </div>

        {/* Quick-select preset buttons */}
        <div className={styles.presets}>
          {PRESETS.map(p => (
            <button
              key={p}
              type="button"
              className={`${styles.presetBtn} ${percentage === String(p) ? styles.presetActive : ''}`}
              onClick={() => setPercentage(String(p))}
            >
              {p}%
            </button>
          ))}
        </div>

        {/* Manual percentage input */}
        <div className={styles.inputRow}>
          <input
            ref={inputRef}
            type="number"
            min="0"
            max="100"
            step="0.1"
            value={percentage}
            onChange={e => setPercentage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Custom %"
            className={styles.percentInput}
          />
          <span className={styles.pctSymbol}>%</span>
        </div>

        {/* Live preview */}
        {discountAmount !== null ? (
          <div className={styles.preview}>
            Discount: <strong>Rs. {discountAmount.toFixed(2)}</strong>
            <span className={styles.previewSub}> ({pct}% of Rs. {subtotal.toFixed(2)})</span>
          </div>
        ) : (
          <div className={styles.previewEmpty}>Enter a percentage above</div>
        )}

        <button
          type="button"
          className={styles.applyBtn}
          onClick={handleApply}
          disabled={discountAmount === null}
        >
          Apply
        </button>
      </div>
    </>,
    document.body
  );
};

export default DiscountPercentagePopup;
